// Backup reminder utilities
// Manages backup reminder preferences and checks if reminder should be shown

const REMINDER_KEY = 'canokey_backup_reminder';
const REMINDER_INTERVAL_DAYS = 7; // Show reminder every 7 days

/**
 * Get backup reminder settings
 * @returns {Object} Reminder settings
 */
export function getReminderSettings() {
  try {
    const settings = localStorage.getItem(REMINDER_KEY);
    return settings ? JSON.parse(settings) : {
      enabled: true,
      lastDismissed: null,
      lastBackup: null
    };
  } catch (error) {
    console.error('Failed to load reminder settings:', error);
    return {
      enabled: true,
      lastDismissed: null,
      lastBackup: null
    };
  }
}

/**
 * Update last backup timestamp
 */
export function updateLastBackup() {
  try {
    const settings = getReminderSettings();
    settings.lastBackup = new Date().toISOString();
    localStorage.setItem(REMINDER_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to update last backup timestamp:', error);
  }
}

/**
 * Dismiss reminder for now
 */
export function dismissReminder() {
  try {
    const settings = getReminderSettings();
    settings.lastDismissed = new Date().toISOString();
    localStorage.setItem(REMINDER_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to dismiss reminder:', error);
  }
}

/**
 * Disable reminder permanently
 */
export function disableReminder() {
  try {
    const settings = getReminderSettings();
    settings.enabled = false;
    localStorage.setItem(REMINDER_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to disable reminder:', error);
  }
}

/**
 * Enable reminder
 */
export function enableReminder() {
  try {
    const settings = getReminderSettings();
    settings.enabled = true;
    localStorage.setItem(REMINDER_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to enable reminder:', error);
  }
}

/**
 * Check if reminder should be shown
 * @returns {boolean} True if reminder should be shown
 */
export function shouldShowReminder() {
  const settings = getReminderSettings();

  // Don't show if disabled
  if (!settings.enabled) {
    return false;
  }

  const now = new Date();

  // Check last backup date
  if (settings.lastBackup) {
    const lastBackup = new Date(settings.lastBackup);
    const daysSinceBackup = Math.floor((now - lastBackup) / (1000 * 60 * 60 * 24));

    // Don't show if backed up recently
    if (daysSinceBackup < REMINDER_INTERVAL_DAYS) {
      return false;
    }
  }

  // Check last dismissed date
  if (settings.lastDismissed) {
    const lastDismissed = new Date(settings.lastDismissed);
    const daysSinceDismissed = Math.floor((now - lastDismissed) / (1000 * 60 * 60 * 24));

    // Don't show if dismissed recently (within 1 day)
    if (daysSinceDismissed < 1) {
      return false;
    }
  }

  // Show reminder if:
  // 1. Never backed up, or
  // 2. Last backup was more than REMINDER_INTERVAL_DAYS ago, and
  // 3. Not dismissed within the last day
  return true;
}

/**
 * Get days since last backup
 * @returns {number|null} Days since last backup, or null if never backed up
 */
export function getDaysSinceLastBackup() {
  const settings = getReminderSettings();

  if (!settings.lastBackup) {
    return null;
  }

  const now = new Date();
  const lastBackup = new Date(settings.lastBackup);
  return Math.floor((now - lastBackup) / (1000 * 60 * 60 * 24));
}
